import { createColumnHelper } from '@tanstack/react-table';
import {
  Calendar as CalendarIcon,
  Eye,
  GraduationCap,
  User,
} from 'lucide-react';
import { useMemo } from 'react';

import type { ScheduledDropResponse } from '~entities/scheduled-drops/api/get-scheduled-drops';

import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSelectFilter,
} from '~shared/components/enrollio-table';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';

import {
  formatScheduledDropDate,
  formatScheduledDropStudentName,
} from './scheduled-drops.helpers';
import {
  SCHEDULED_DROP_CREATED_BY,
  SCHEDULED_DROP_STATUS,
  type ScheduledDropsTableProps,
} from './scheduled-drops.types';

const columnHelper = createColumnHelper<ScheduledDropResponse>();

function ScheduledDropStatusBadge({
  status,
}: {
  status: ScheduledDropResponse['status'];
}) {
  switch (status) {
    case SCHEDULED_DROP_STATUS.PENDING:
      return (
        <Badge variant='outline' className='bg-blue-50 text-blue-700'>
          Scheduled
        </Badge>
      );
    case SCHEDULED_DROP_STATUS.EXECUTED:
      return (
        <Badge variant='outline' className='bg-green-50'>
          Executed
        </Badge>
      );
    case SCHEDULED_DROP_STATUS.CANCELLED:
      return (
        <Badge variant='outline' className='bg-gray-50'>
          Cancelled
        </Badge>
      );
    default:
      return <Badge>{status}</Badge>;
  }
}

export function ScheduledDropsTable({
  drops,
  onCancel,
  onViewReason,
}: ScheduledDropsTableProps) {
  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => formatScheduledDropStudentName(row), {
        id: 'Student',
        header: 'Student',
        enableSorting: false,
        cell: (info) => (
          <div className='flex items-center gap-2'>
            <User className='size-4 shrink-0 text-muted-foreground' />
            <p className='min-w-0 break-words font-medium'>{info.getValue()}</p>
          </div>
        ),
      }),
      columnHelper.accessor((row) => row.enrollment?.name ?? row.enrollmentId, {
        id: 'Class/Enrollment',
        header: 'Class/Enrollment',
        enableSorting: false,
        cell: (info) => (
          <div className='flex items-center gap-2'>
            <GraduationCap className='size-4 shrink-0 text-muted-foreground' />
            <span className='min-w-0 break-words'>{info.getValue()}</span>
          </div>
        ),
      }),
      columnHelper.accessor('scheduledDropDate', {
        id: 'Scheduled Drop Date',
        header: 'Scheduled Drop Date',
        enableSorting: false,
        cell: (info) => (
          <div className='flex items-center gap-2'>
            <CalendarIcon className='size-4 shrink-0 text-muted-foreground' />
            {formatScheduledDropDate(info.getValue())}
          </div>
        ),
      }),
      columnHelper.accessor('status', {
        id: 'Status',
        header: 'Status',
        enableSorting: false,
        cell: (info) => <ScheduledDropStatusBadge status={info.getValue()} />,
      }),
      columnHelper.accessor('shouldRefund', {
        id: 'Refund',
        header: 'Refund',
        enableSorting: false,
        cell: (info) =>
          info.getValue() ? (
            <Badge variant='secondary'>Yes</Badge>
          ) : (
            <span className='text-sm text-muted-foreground'>No</span>
          ),
      }),
      columnHelper.accessor('createdBy', {
        id: 'Created By',
        header: 'Created By',
        enableSorting: false,
        cell: (info) => (
          <Badge variant='outline'>
            {info.getValue() === SCHEDULED_DROP_CREATED_BY.STUDIO
              ? 'Studio'
              : 'Parent'}
          </Badge>
        ),
      }),
      columnHelper.accessor((row) => row.dropReason || 'View', {
        id: 'Reason',
        header: 'Reason',
        enableSorting: false,
        cell: (info) => (
          <Button
            variant='ghost'
            size='sm'
            onClick={() => {
              onViewReason(info.row.original);
            }}
          >
            <Eye className='mr-1 size-4' />
            View
          </Button>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const drop = info.row.original;
          if (drop.status !== SCHEDULED_DROP_STATUS.PENDING) {
            return null;
          }

          return (
            <Button
              variant='destructive'
              size='sm'
              onClick={() => {
                onCancel(drop);
              }}
            >
              Cancel
            </Button>
          );
        },
      }),
    ],
    [onCancel, onViewReason],
  );

  return (
    <EnrollioTable
      ariaLabel='Scheduled drops'
      totalLabel='Scheduled drops'
      filterHeader={(table) => {
        const statusColumn = table.getColumn('Status');
        const status = statusColumn?.getFilterValue();

        return (
          <EnrollioTableSelectFilter
            value={typeof status === 'string' ? status : 'all'}
            onChange={(value) => {
              statusColumn?.setFilterValue(value === 'all' ? undefined : value);
            }}
            placeholder='Filter by status'
            options={[
              { label: 'All statuses', value: 'all' },
              { label: 'Scheduled', value: SCHEDULED_DROP_STATUS.PENDING },
              { label: 'Executed', value: SCHEDULED_DROP_STATUS.EXECUTED },
              { label: 'Cancelled', value: SCHEDULED_DROP_STATUS.CANCELLED },
            ]}
          />
        );
      }}
      toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
      className='min-w-0'
      columns={columns}
      data={drops}
      getRowId={(row) => row.id}
    />
  );
}
